import React, { useState } from 'react';
import styles from './AiEngine.module.scss';

const PARTICLES = [
  [12, 22],
  [27, 74],
  [80, 18],
  [88, 68],
  [50, 90],
  [17, 48],
  [83, 46],
  [45, 10],
];

const ICON = {
  bolt: <path d="M13 2 4 14h7l-1 8 9-12h-7z" />,
  tag: (
    <>
      <path d="M3 11l8-8 9 .5.5 9-8 8z" />
      <circle cx="8.5" cy="8.5" r="1.5" />
    </>
  ),
  brain: (
    <>
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
      <circle cx="12" cy="12" r="2" />
      <path d="M9 3v2M15 3v2M9 19v2M15 19v2M3 9h2M3 15h2M19 9h2M19 15h2" />
    </>
  ),
  sliders: (
    <>
      <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h12M20 18h0" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="18" cy="18" r="2" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l7 3v5c0 4.2-3 7.2-7 8-4-.8-7-3.8-7-8V6z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  box: (
    <>
      <path d="M12 3 4 7v10l8 4 8-4V7z" />
      <path d="M4 7l8 4 8-4M12 11v10" />
    </>
  ),
  chat: (
    <>
      <path d="M4 5h16v11H9l-5 4z" />
      <path d="M8 10h8M8 13h5" />
    </>
  ),
  db: (
    <>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v12c0 1.6 3.1 3 7 3s7-1.4 7-3V6" />
      <path d="M5 12c0 1.6 3.1 3 7 3s7-1.4 7-3" />
    </>
  ),
  activity: <path d="M3 12h4l3 7 4-15 3 8h4" />,
  workflow: (
    <>
      <rect x="3" y="3" width="6" height="6" rx="1.5" />
      <rect x="15" y="15" width="6" height="6" rx="1.5" />
      <path d="M9 6h6a3 3 0 0 1 3 3v6" />
    </>
  ),
  audit: (
    <>
      <rect x="6" y="4" width="12" height="17" rx="2" />
      <path d="M9.5 4V3h5v1" />
      <path d="M9 13l2 2 4-4" />
    </>
  ),
  truck: (
    <>
      <path d="M3 6h11v9H3z" />
      <path d="M14 9h4l3 3v3h-7z" />
      <circle cx="7" cy="18" r="1.6" />
      <circle cx="17.5" cy="18" r="1.6" />
    </>
  ),
};

const H = (sx: number, sy: number, ex: number, ey: number) =>
  `M${sx} ${sy}C${(sx + ex) / 2} ${sy} ${(sx + ex) / 2} ${ey} ${ex} ${ey}`;
const V = (sx: number, sy: number, ex: number, ey: number) =>
  `M${sx} ${sy}C${sx} ${(sy + ey) / 2} ${ex} ${(sy + ey) / 2} ${ex} ${ey}`;

interface Node {
  label: string;
  icon: React.ReactNode;
  cls: string;
  d: string; // desktop trace
  ax: number;
  ay: number;
  md: string; // mobile trace
  mx: number;
  my: number;
}

// mobile portrait helpers (viewBox 600 x 1000), chip centre (300,500)
const mLeft = (o: number) => {
  const nodeY = 70 + o * 150;
  const pinY = 445 + o * 24;
  return { d: H(235, pinY, 136, nodeY), ax: 136, ay: nodeY };
};
const mRight = (o: number) => {
  const nodeY = 70 + o * 150;
  const pinY = 445 + o * 24;
  return { d: H(365, pinY, 464, nodeY), ax: 464, ay: nodeY };
};

const NODES: Node[] = [
  { label: 'Autonomous Commerce Engines', icon: ICON.bolt, cls: 'n1', d: V(475, 235, 300, 120), ax: 300, ay: 120, md: mLeft(0).d, mx: mLeft(0).ax, my: mLeft(0).ay },
  { label: 'Business Monitoring Agents', icon: ICON.activity, cls: 'n2', d: V(500, 235, 500, 100), ax: 500, ay: 100, md: mLeft(1).d, mx: mLeft(1).ax, my: mLeft(1).ay },
  { label: 'Brand Intelligence', icon: ICON.shield, cls: 'n3', d: V(525, 235, 700, 120), ax: 700, ay: 120, md: mRight(0).d, mx: mRight(0).ax, my: mRight(0).ay },
  { label: 'Competitor Price Monitoring', icon: ICON.tag, cls: 'n4', d: H(435, 262, 172, 185), ax: 172, ay: 185, md: mLeft(2).d, mx: mLeft(2).ax, my: mLeft(2).ay },
  { label: 'Retail Intelligence & Co-pilots', icon: ICON.brain, cls: 'n5', d: H(435, 300, 137, 300), ax: 137, ay: 300, md: mLeft(3).d, mx: mLeft(3).ax, my: mLeft(3).ay },
  { label: 'Workflow Embeddings', icon: ICON.workflow, cls: 'n6', d: H(435, 338, 172, 415), ax: 172, ay: 415, md: mLeft(4).d, mx: mLeft(4).ax, my: mLeft(4).ay },
  { label: 'Warehouse Automations', icon: ICON.box, cls: 'n7', d: H(565, 262, 828, 185), ax: 828, ay: 185, md: mRight(1).d, mx: mRight(1).ax, my: mRight(1).ay },
  { label: 'Data Lake for LLMs', icon: ICON.db, cls: 'n8', d: H(565, 300, 863, 300), ax: 863, ay: 300, md: mRight(2).d, mx: mRight(2).ax, my: mRight(2).ay },
  { label: 'eCommerce Benchmarking & Audits', icon: ICON.audit, cls: 'n9', d: H(565, 338, 828, 415), ax: 828, ay: 415, md: mRight(3).d, mx: mRight(3).ax, my: mRight(3).ay },
  { label: 'Marketplace Controllers', icon: ICON.sliders, cls: 'n10', d: V(475, 365, 300, 472), ax: 300, ay: 472, md: mLeft(5).d, mx: mLeft(5).ax, my: mLeft(5).ay },
  { label: 'Customer Engagement & Sales', icon: ICON.chat, cls: 'n11', d: V(500, 365, 500, 492), ax: 500, ay: 492, md: mRight(4).d, mx: mRight(4).ax, my: mRight(4).ay },
  { label: 'Logistic Automations', icon: ICON.truck, cls: 'n12', d: V(525, 365, 700, 472), ax: 700, ay: 472, md: mRight(5).d, mx: mRight(5).ax, my: mRight(5).ay },
];

const SIDE_PINS = [262, 290, 318, 346];
const EDGE_PINS = [470, 490, 510, 530];

const AiEngine = () => {
  const [active, setActive] = useState<number | null>(null);

  const traceCls = (i: number) =>
    active === null ? '' : active === i ? styles.lineActive : styles.lineDim;
  const pulseCls = (i: number) =>
    active === null ? '' : active === i ? styles.pulseActive : styles.pulseDim;

  return (
    <section className={styles.engine}>
      <div className={styles.head}>
      <p className={styles.eyebrow}>Powered by Socommerz AI</p>
      <h2 className={styles.title}>
        One AI engine.{' '}
        <span className={styles.accent}>Every commerce capability.</span>
      </h2>
      <p className={styles.subtitle}>
        From autonomous pricing to LLM-ready data lakes — every Socommerz
        capability runs on a single intelligent core.
      </p>
    </div>

    <div className={styles.stage}>
      {/* ambient depth: pulsing rings + floating particles */}
      <div className={styles.rings} aria-hidden="true">
        <span className={styles.ring} />
        <span className={styles.ring} />
        <span className={styles.ring} />
      </div>
      <div className={styles.particles} aria-hidden="true">
        {PARTICLES.map(([x, y], i) => (
          <span
            key={i}
            className={styles.particle}
            style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.6}s` }}
          />
        ))}
      </div>

      {/* desktop traces */}
      <svg
        className={`${styles.traces} ${styles.tracesDesktop}`}
        viewBox="0 0 1000 600"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        {NODES.map((n, i) => (
          <path key={`b${i}`} className={`${styles.trace} ${traceCls(i)}`} d={n.d} />
        ))}
        {NODES.map((n, i) => (
          <path key={`p${i}`} className={`${styles.pulse} ${pulseCls(i)}`} d={n.d} pathLength={100} style={{ animationDelay: `${i * 0.3}s` } as React.CSSProperties} />
        ))}
        {SIDE_PINS.map((y, i) => (
          <rect key={`pl${i}`} className={styles.pin} x={429} y={y - 3} width={10} height={6} rx={2} />
        ))}
        {SIDE_PINS.map((y, i) => (
          <rect key={`pr${i}`} className={styles.pin} x={561} y={y - 3} width={10} height={6} rx={2} />
        ))}
        {EDGE_PINS.map((x, i) => (
          <rect key={`pt${i}`} className={styles.pin} x={x - 3} y={229} width={6} height={9} rx={2} />
        ))}
        {EDGE_PINS.map((x, i) => (
          <rect key={`pb${i}`} className={styles.leg} x={x - 3} y={363} width={6} height={16} rx={2} />
        ))}
        {NODES.map((n, i) => (
          <rect key={`nb${i}`} className={styles.nub} x={n.ax - 6} y={n.ay - 6} width={12} height={12} rx={4} />
        ))}
      </svg>

      {/* mobile (portrait) traces */}
      <svg
        className={`${styles.traces} ${styles.tracesMobile}`}
        viewBox="0 0 600 1000"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        {NODES.map((n, i) => (
          <path key={`mb${i}`} className={`${styles.trace} ${traceCls(i)}`} d={n.md} />
        ))}
        {NODES.map((n, i) => (
          <path key={`mp${i}`} className={`${styles.pulse} ${pulseCls(i)}`} d={n.md} pathLength={100} style={{ animationDelay: `${i * 0.3}s` } as React.CSSProperties} />
        ))}
        {NODES.map((n, i) => (
          <rect key={`mn${i}`} className={styles.nub} x={n.mx - 6} y={n.my - 6} width={12} height={12} rx={4} />
        ))}
      </svg>

      {/* central chip */}
      <div className={`${styles.chip} ${active !== null ? styles.chipActive : ''}`}>
        <span className={styles.chipGlow} aria-hidden="true" />
        <span className={styles.chipRing} aria-hidden="true" />
        <span className={styles.chipInner}>
          <span className={styles.chipScan} aria-hidden="true" />
          <img src="/logo2.svg" alt="Socommerz" className={styles.chipLogo} />
          <span className={styles.chipBrand}>SOCOMMERZ</span>
          <span className={styles.chipAi}>AI</span>
        </span>
      </div>

      {/* nodes */}
      {NODES.map((node, i) => (
        <div
          key={node.label}
          className={`${styles.node} ${styles[node.cls]}`}
          onMouseEnter={() => setActive(i)}
          onMouseLeave={() => setActive(null)}
          onFocus={() => setActive(i)}
          onBlur={() => setActive(null)}
          tabIndex={0}
        >
          <span className={styles.nodeTile}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {node.icon}
            </svg>
          </span>
          <span className={styles.nodeLabel}>{node.label}</span>
        </div>
      ))}
      </div>
    </section>
  );
};

export default AiEngine;
